// The stopwatch behind handling time: how long someone actually spent on one
// invoice, as opposed to how long the invoice sat somewhere.
//
// WHY THE CLOCK IS HERE AND NOT ON THE SERVER. Only the browser knows whether
// its tab was visible and whether anybody was typing. A server can only measure
// the gap between two requests, which counts lunch.
//
// THE RULE IS: time counts while the tab is VISIBLE and the user has been ACTIVE
// within the last ninety seconds. Walk away and the clock stops ninety seconds
// later; switch to another tab and it stops at once. Ninety is long enough to
// read a page of a scanned invoice without being called idle and short enough
// that a trip to the printer does not land in the measure.
//
// THIS FILE HAS NO REACT, NO DOM AND NO CLOCK OF ITS OWN. Every method takes
// `now` from the caller. That is what makes the rules testable without a
// browser, which is the whole reason the accumulator is separate from the hook
// that drives it (`useInvoiceTimer` in `hooks.ts`).
//
// NOTHING RENDERS FROM THIS. The elapsed value is never displayed, so no
// component re-renders on a timer; the accumulator is read once, at the end.

/**
 * WHICH OPENS ARE TIMED (code review H-02, 2026-10-17): an open at a pipeline
 * step, and nothing else. The two read-only lookups — Archives, and the
 * Dashboard's listing, which mounts the modal view-only — file no session, so
 * reading an invoice there is neither handling time nor a touch.
 */
export function isTimedOpen(screen: string, viewOnly: boolean): boolean {
  return !viewOnly && screen !== 'archives'
}

/** Seconds without a keystroke, click or scroll before the clock stops. */
export const IDLE_THRESHOLD_SECONDS = 90

/**
 * The hard ceiling on one session, matching `MAX_ACTIVE_SECONDS` in the
 * backend's `handlingTime.ts` and the `view_sessions_seconds_sane` CHECK.
 *
 * Applied here too, rather than left to the server: a client that ships an
 * absurd number gets it corrected before it is sent, so `was_capped` on the
 * stored row means the reading was genuinely long rather than that the browser
 * misbehaved in some way nobody logged.
 */
export const MAX_ACTIVE_SECONDS = 1800

/** What one finished session reports. Seconds are whole. */
export interface HandlingSpan {
  opened_at: string
  ended_at: string
  active_seconds: number
  idle_seconds: number
}

/**
 * An accumulator for one invoice's viewing session.
 *
 * Milliseconds throughout, rounded to seconds only at the end — accumulating
 * rounded seconds would lose up to half a second per transition, and a session
 * of twenty short glances would drift by ten.
 */
export class HandlingTimer {
  private readonly openedAtMs: number
  /** Milliseconds counted so far. */
  private activeMs = 0
  /**
   * When the current counting run began, or `null` when the clock is stopped.
   * The constructor starts it running: opening the modal IS the first action.
   */
  private runningSinceMs: number | null = null
  /** The last moment the user did something. Seeded at open, which IS an action. */
  private lastActivityMs: number
  private ended = false
  /** Frozen at the first `end`, so a repeated flush reports the same span. */
  private endedAtMs: number | null = null

  constructor(openedAtMs: number) {
    this.openedAtMs = openedAtMs
    this.lastActivityMs = openedAtMs
    this.runningSinceMs = openedAtMs
  }

  /**
   * Fold everything counted up to `nowMs` into the total and stop the clock.
   *
   * THE IDLE CUT IS APPLIED AT THE MOMENT OF SETTLING, not by a background
   * timer. If the user went quiet at 10:00 and the tab hides at 10:05, the run
   * is credited to 10:01:30 — the moment the idle threshold expired — and the
   * remaining three and a half minutes are idle. That is why no interval timer
   * is needed to enforce the threshold: the arithmetic is retrospective.
   */
  private settle(nowMs: number): void {
    if (this.runningSinceMs === null) return
    const deadline = this.lastActivityMs + IDLE_THRESHOLD_SECONDS * 1000
    const countUntil = Math.min(nowMs, deadline)
    if (countUntil > this.runningSinceMs) this.activeMs += countUntil - this.runningSinceMs
    this.runningSinceMs = null
  }

  /** The user did something: a key, a click, a scroll. */
  activity(nowMs: number): void {
    if (this.ended) return
    // Settle BEFORE moving the activity mark, or a keystroke after two minutes
    // of silence would retroactively count that silence as work.
    this.settle(nowMs)
    this.lastActivityMs = nowMs
    this.runningSinceMs = nowMs
  }

  /** The tab became visible again. Counts as activity: they came back to look. */
  resume(nowMs: number): void {
    this.activity(nowMs)
  }

  /** The tab was hidden. Stop counting immediately — no idle grace. */
  pause(nowMs: number): void {
    if (this.ended) return
    this.settle(nowMs)
  }

  /**
   * Close the session and report it. Idempotent: a second call returns the same
   * span, because the close flush and the tab-hidden flush can both fire and
   * the second must not report a fresh zero-second session.
   */
  end(nowMs: number): HandlingSpan {
    if (!this.ended) {
      this.settle(nowMs)
      this.endedAtMs = nowMs
      this.ended = true
    }
    const endedAtMs = this.endedAtMs ?? nowMs
    const active = Math.min(Math.round(this.activeMs / 1000), MAX_ACTIVE_SECONDS)
    const wall = Math.max(0, Math.round((endedAtMs - this.openedAtMs) / 1000))
    return {
      opened_at: new Date(this.openedAtMs).toISOString(),
      ended_at: new Date(endedAtMs).toISOString(),
      active_seconds: active,
      // Whatever was not active. `Math.max` guards the rounding boundary, where
      // active can land one second above the wall clock.
      idle_seconds: Math.max(0, wall - active),
    }
  }

  get isEnded(): boolean {
    return this.ended
  }
}

/** Where a client session id comes from. Extracted so a test can make it predictable. */
export function newSessionId(): string {
  // `randomUUID` needs a secure context. The app is only ever served over
  // HTTPS, but the fallback keeps a plain-http dev page from throwing on close
  // — a lost measurement is acceptable, an exception in an unload handler is
  // not, because it can abort the flush of the other sessions beside it.
  const uuid = globalThis.crypto as { randomUUID?: () => string } | undefined
  if (typeof uuid?.randomUUID === 'function') return uuid.randomUUID()
  const hex = (n: number): string =>
    Array.from({ length: n }, () => Math.floor(Math.random() * 16).toString(16)).join('')
  return `${hex(8)}-${hex(4)}-4${hex(3)}-8${hex(3)}-${hex(12)}`
}
